// ============================================================================
// TeamHealthBoard — tableau sante d'equipe (L10, F-169/F-170, M-173)
// ============================================================================
// Portage du PoC team-health-board.tsx : presentation SEULE — tous les calculs
// viennent de lib/member-pulse (buildMemberHealthRow / teamMonthlyAverages), deja
// porte ET teste. On le CONSOMME, on ne le re-porte pas.
//
// M-173 : ecart de perception = charge auto (avg 3 derniers sondages) − note
// `workload` du check-in gestionnaire le plus recent ; tri risque = avg3Workload
// ASC (le plus a risque en tete). Les deux echelles vont de 1 (a risque) a 5.
// Un ecart NEGATIF = l'employe se sent PLUS charge que ce que le gestionnaire
// percoit — c'est le signal qui guide le 1:1.
//
// PII : le `comment` du pulse EST affiche ici (title/hover, parite PoC) — la page
// est deja director|admin scopee (M-150/M-151) et le detail narratif y est permis
// (M-011), contrairement au roster (M-154).

import { EmptyState, StatusBadge } from '@gs/ui';
import { HeartPulse } from 'lucide-react';
import { useTranslation } from '../../i18n';
import {
  buildMemberHealthRow, teamMonthlyAverages, type MemberHealthRow,
} from '../../lib/member-pulse/aggregate';
import type { MemberPulse as ApiMemberPulse, Checkin, RosterMember } from '../../hooks/useMonEquipe';
import type { EmployeeCheckin, MemberPulse } from '../../lib/types/firestore';

/** Adapte les DTO camelCase de l'API a la forme snake_case des libs portees. */
function toLibPulse(p: ApiMemberPulse): MemberPulse {
  return {
    id: p.id,
    email: p.email,
    team_id: null,
    period: p.period,
    workload_score: p.workloadScore,
    energy_score: p.energyScore,
    comment: p.comment,
    submitted_at: p.submittedAt,
  };
}

function toLibCheckin(c: Checkin): EmployeeCheckin {
  return {
    id: c.id,
    email: c.email,
    team_id: null,
    year: c.year,
    quarter: c.quarter as EmployeeCheckin['quarter'],
    answers: c.answers.map((a) => ({ key: a.key, label: a.label, rating: a.rating })),
    note: c.note,
    created_by_email: c.createdByEmail,
    created_at: c.createdAt,
    updated_at: c.updatedAt,
  };
}

/** Score 1..5 -> variant DS. 1-2 = a risque, 3 = a surveiller, 4-5 = confortable. */
function scoreVariant(score: number | null): 'success' | 'warning' | 'danger' | 'neutral' {
  if (score == null) return 'neutral';
  if (score <= 2) return 'danger';
  if (score < 4) return 'warning';
  return 'success';
}

function formatScore(locale: string, value: number | null): string {
  if (value == null) return '—';
  return new Intl.NumberFormat(locale.startsWith('en') ? 'en-CA' : 'fr-CA', {
    minimumFractionDigits: 1, maximumFractionDigits: 1,
  }).format(value);
}

export function TeamHealthBoard({
  roster,
  pulses,
  checkins,
}: {
  roster: RosterMember[];
  pulses: ApiMemberPulse[];
  checkins: Checkin[];
}) {
  const { t, i18n } = useTranslation();

  // F-169 : aucun pulse -> EmptyState (etat natif du board, pas une erreur).
  if (pulses.length === 0) {
    return (
      <EmptyState
        icon={<HeartPulse size={48} aria-hidden="true" />}
        title={t('gest:sante.empty.title')}
        description={t('gest:sante.empty.description')}
      />
    );
  }

  const libPulses = pulses.map(toLibPulse);
  const libCheckins = checkins.map(toLibCheckin);

  // M-152 : le roster ne porte que les ACTIFS — un pulse d'ex-membre ne pese pas.
  const rows: Array<MemberHealthRow & { fullName: string | null }> = roster
    .map((member) => ({
      ...buildMemberHealthRow(
        member.email,
        libPulses.filter((p) => p.email.toLowerCase() === member.email.toLowerCase()),
        libCheckins.filter((c) => c.email.toLowerCase() === member.email.toLowerCase()),
      ),
      fullName: member.fullName,
    }))
    // M-173 : tri risque = avg3Workload ASC. Les membres sans sondage vont en fin
    // (on ne peut rien dire d'eux — les placer « a risque » serait faux).
    .sort((a, b) => {
      if (a.avg3Workload == null) return 1;
      if (b.avg3Workload == null) return -1;
      return a.avg3Workload - b.avg3Workload;
    });

  const monthly = teamMonthlyAverages(libPulses);
  const latestMonth = monthly.at(-1) ?? null;

  return (
    <div className="space-y-5">
      {/* KPI d'equipe du mois le plus recent (F-170). */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-xl bg-white p-4 shadow-sm dark:bg-slate-800">
          <p className="text-meta text-slate-500 dark:text-slate-400">{t('gest:sante.kpis.workload')}</p>
          <p className="mt-1 text-head font-semibold tabular-nums text-slate-900 dark:text-slate-100">
            {formatScore(i18n.language, latestMonth?.workload ?? null)}
          </p>
        </div>
        <div className="rounded-xl bg-white p-4 shadow-sm dark:bg-slate-800">
          <p className="text-meta text-slate-500 dark:text-slate-400">{t('gest:sante.kpis.energy')}</p>
          <p className="mt-1 text-head font-semibold tabular-nums text-slate-900 dark:text-slate-100">
            {formatScore(i18n.language, latestMonth?.energy ?? null)}
          </p>
        </div>
        <div className="rounded-xl bg-white p-4 shadow-sm dark:bg-slate-800">
          <p className="text-meta text-slate-500 dark:text-slate-400">{t('gest:sante.kpis.responses')}</p>
          <p className="mt-1 text-head font-semibold tabular-nums text-slate-900 dark:text-slate-100">
            {latestMonth ? latestMonth.count : 0}
          </p>
        </div>
      </div>

      {/* Matrice membres x sante (F-170) — table maison, scroll horizontal CONTENU
          dans son conteneur (le body ne scrolle jamais horizontalement).
          `min-w-max` (classe NOMMEE) plutot qu'une largeur arbitraire — regle #28 /
          DS-015, meme precedent que finance/IncomeStatementTable. */}
      <div className="overflow-x-auto rounded-xl bg-white shadow-sm dark:bg-slate-800">
        <table className="w-full min-w-max text-left">
          <thead>
            <tr className="border-b border-slate-200 text-meta text-slate-500 dark:border-slate-700 dark:text-slate-400">
              <th scope="col" className="px-4 py-3 font-medium">{t('gest:sante.columns.member')}</th>
              <th scope="col" className="px-4 py-3 font-medium">{t('gest:sante.columns.workload')}</th>
              <th scope="col" className="px-4 py-3 font-medium">{t('gest:sante.columns.energy')}</th>
              <th scope="col" className="px-4 py-3 font-medium">{t('gest:sante.columns.manager')}</th>
              <th scope="col" className="px-4 py-3 font-medium">{t('gest:sante.columns.gap')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.email} className="border-b border-slate-100 last:border-0 dark:border-slate-700">
                <td className="px-4 py-3">
                  <p className="font-medium text-slate-900 dark:text-slate-100">{row.fullName ?? row.email}</p>
                  {/* PII permise ici (page scopee, M-011) : le commentaire du dernier
                      sondage en survol — jamais sur le roster (M-154). */}
                  {row.latest?.comment ? (
                    <p
                      className="truncate text-meta text-slate-500 dark:text-slate-400"
                      title={row.latest.comment}
                    >
                      {row.latest.comment}
                    </p>
                  ) : null}
                </td>
                <td className="px-4 py-3">
                  <StatusBadge variant={scoreVariant(row.avg3Workload)}>
                    {formatScore(i18n.language, row.avg3Workload)}
                  </StatusBadge>
                </td>
                <td className="px-4 py-3">
                  <StatusBadge variant={scoreVariant(row.avg3Energy)}>
                    {formatScore(i18n.language, row.avg3Energy)}
                  </StatusBadge>
                </td>
                <td className="px-4 py-3 tabular-nums text-slate-700 dark:text-slate-200">
                  {row.managerWorkload ?? '—'}
                </td>
                <td className="px-4 py-3">
                  {/* M-173 : ecart NEGATIF = l'employe se sent plus charge que percu. */}
                  {row.workloadGap == null ? (
                    <span className="text-slate-400 dark:text-slate-500">—</span>
                  ) : (
                    <span
                      className={`tabular-nums font-medium ${
                        row.workloadGap <= -1
                          ? 'text-red-600 dark:text-red-400'
                          : 'text-slate-700 dark:text-slate-200'
                      }`}
                      title={t('gest:sante.gapHint')}
                    >
                      {row.workloadGap > 0 ? '+' : ''}
                      {formatScore(i18n.language, row.workloadGap)}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-meta text-slate-500 dark:text-slate-400">{t('gest:sante.legend')}</p>
    </div>
  );
}
